import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ICON_TYPE } from '../../icons/icon.model';


@Component({
  selector: 'app-custom-file-uploader',
  templateUrl: './custom-file-uploader.component.html',
  standalone: false
})
export class CustomFileUploaderComponent implements OnInit, OnDestroy {

  @ViewChild('fileUploadInput') fileUploadInput: ElementRef;

  @Input() label = '';

  @Input()
  get required(): boolean {
    return this._required
  };

  set required(required: boolean) {
    this._required = required || false;
    if (this.formGroup != null) {
      this.formGroup.setControl(
        this.formControlName,
        new UntypedFormControl('',
          [this._required ? this.fileRequiredValidator(null) : Validators.nullValidator,
            this.maxFileSize ? this.fileSizeValidator(null, this.maxFileSize) : Validators.nullValidator]));
      this.control = this.formGroup.controls[this.formControlName];
      this.createNewFormChangeSubscription();
      this.formGroup.updateValueAndValidity();
    }
  }

  @Input() formGroup: UntypedFormGroup;
  @Input() formControlName = 'files';
  @Input() maxFileSize: number;

  control: AbstractControl;
  fileName: string;
  fileSize: number;
  fileExtension: string;
  loaded = false;
  valid = true;
  iconTypes = ICON_TYPE;
  private files: FileList;
  private formChangeSubscription: Subscription = new Subscription();
  private _required: boolean;

  constructor() {
  }

  ngOnInit(): void {
    if (this.formGroup != null) {
      this.formGroup.addControl(
        this.formControlName,
        new UntypedFormControl('',
          [this._required ? this.fileRequiredValidator(null) : Validators.nullValidator,
            this.maxFileSize ? this.fileSizeValidator(null, this.maxFileSize) : Validators.nullValidator]));
      this.formGroup.updateValueAndValidity();
      this.control = this.formGroup.controls[this.formControlName];
      this.createNewFormChangeSubscription();
    }
  }

  ngOnDestroy(): void {
    if (this.formChangeSubscription) {
      this.formChangeSubscription.unsubscribe();
    }
  }

  onFileChange(files: FileList): void {
    const reader = new FileReader();
    const file = files?.item(0);
    this.fileName = file?.name;
    this.fileSize = file?.size;
    this.fileExtension = this.fileName?.split('.').pop();
    this.loaded = true;
    this.files = files;

    this.formGroup.setControl(
      this.formControlName,
      new UntypedFormControl('',
        [this._required ? this.fileRequiredValidator(files) : Validators.nullValidator,
          this.maxFileSize ? this.fileSizeValidator(files, this.maxFileSize) : Validators.nullValidator]));
    this.control = this.formGroup.controls[this.formControlName];
    this.createNewFormChangeSubscription();
    this.formGroup.updateValueAndValidity();
    this.control.markAsTouched();

    if (files && files.length) {
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        // File as string of bytes without metadata
        let file64encoded = e.target.result.toString().replace(/^data:(.*,)?/, '');
        if ((file64encoded.length % 4) > 0) {
          file64encoded += '='.repeat(4 - (file64encoded.length % 4));
        }
        this.control?.patchValue({
          file: file64encoded,
          name: file.name
        });
        this.valid = this.control ? this.control.valid : true;
      };
    } else {
      this.reset();
    }
  }

  private fileSizeValidator(files: FileList, maxFileSize: number): any {
    return () => {
      if (files) {
        const fileSize = files.item(0)?.size;
        const fileSizeInKB = Math.round(fileSize / 1024);
        return (fileSizeInKB >= maxFileSize) ? { maxFileSize: true } : null;
      }
      return null;
    };
  }

  private fileRequiredValidator(files: FileList): any {
    return () => (files && files.length) ? null : { required: true };
  }

  private removeImage(): void {
    this.fileUploadInput.nativeElement.value = "";
    this.files = null;
    this.fileName = null;
    this.fileSize = null;
    this.fileExtension = null;
    this.valid = true;
    this.loaded = false;
  }

  private reset(): void {
    this.files = null;
    this.fileName = null;
    this.fileSize = null;
    this.fileExtension = null;
    this.valid = true;

    this.formGroup.setControl(
      this.formControlName,
      new UntypedFormControl('',
        [this._required ? this.fileRequiredValidator(null) : Validators.nullValidator,
          this.maxFileSize ? this.fileSizeValidator(null, this.maxFileSize) : Validators.nullValidator]));
    this.control = this.formGroup.controls[this.formControlName];
    this.createNewFormChangeSubscription();
  }

  private createNewFormChangeSubscription(): void {
    this.formChangeSubscription.add(this.control.valueChanges.subscribe(next => this.checkControl()));
  }

  private checkControl() {
    if (!this.control.value) {
      this.reset();
    }
  }
}
